/**
 * Determine if a session is in check-in mode
 * Check-in mode: Session is in the future OR within 2 hours of start time
 * @param scheduledAt - ISO datetime string of session start time
 * @returns true if check-in mode, false if review mode
 */
export function isCheckInMode(scheduledAt: string): boolean {
    const sessionDate = new Date(scheduledAt);
    const now = new Date();
    const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

    // If session is in the future, it's check-in mode
    if (sessionDate > now) {
        return true;
    }

    // If session is in the past but within 2 hours, it's still check-in mode
    const timeSinceSession = now.getTime() - sessionDate.getTime();
    return timeSinceSession <= twoHoursInMs;
}

/**
 * Get the default mode for a session
 * @param scheduledAt - ISO datetime string of session start time
 * @returns "check-in" or "review"
 */
export function getDefaultMode(scheduledAt: string): "check-in" | "review" {
    return isCheckInMode(scheduledAt) ? "check-in" : "review";
}

/** Schedule phase for book session list cards (badges + attendance copy). */
export type SessionSchedulePhase = "upcoming" | "inProgress" | "completed";

/**
 * - upcoming: session start is in the future
 * - inProgress: start has passed but still within check-in window (see isCheckInMode)
 * - completed: session is past the check-in window
 */
export function getSessionSchedulePhase(scheduledAt: string): SessionSchedulePhase {
    const sessionDate = new Date(scheduledAt);
    const now = new Date();
    if (sessionDate > now) {
        return "upcoming";
    }
    if (isCheckInMode(scheduledAt)) {
        return "inProgress";
    }
    return "completed";
}

/** Booking fill 0–100; safe when capacity is 0. */
export function getBookingFillPercent(totalBookings: number, capacity: number): number {
    if (capacity <= 0) {
        return 0;
    }
    return Math.max(0, Math.min(100, (totalBookings / capacity) * 100));
}

/** Turnout (attended ÷ booked) 0–100, or null when there are no bookings. */
export function getTurnoutPercent(totalAttendees: number, totalBookings: number): number | null {
    if (totalBookings <= 0) {
        return null;
    }
    return Math.max(0, Math.min(100, (totalAttendees / totalBookings) * 100));
}
